/** The Pre-check-in tab's "preview" (backend plan, P4a): a client-side plan of the draft —
 *  there is no changeset endpoint, and the Ledger saves only on a plan of the current body.
 *  Synchronous: the plan is there the moment the body is.
 *
 *  - changes: one outcome per change, with what it does in words (`notes`): fields added,
 *    removed, relabelled, required, moved; waivers added and removed; custom fields
 *    relabelled, retyped, removed;
 *  - conflicts: what the server would refuse, on the change that carries it — mirrored here
 *    so Save says why first: a field without a label or one too long, more than 30 custom
 *    fields in a form, a question about a category the operator does not have, a waiver
 *    that is not the operator's (or disabled and new, or listed twice), a custom field's
 *    type changed once answers exist, or one removed that is still used;
 *  - follow_ups ("On save"): no settings stored yet → saving creates them; the tours that
 *    inherit get the change; a relabelled or retyped custom field reaches every tour that
 *    asks it (named, with the count — confirmed before saving: `CONFIRM_FOLLOW_UPS`); a
 *    relabelled standard field relabels it on every tour of this operator that asks it
 *    (named, like a custom field's; no count — the GET has none — and not confirmed). */
import { useMemo } from 'react';

import { asFields, isQuestion, questionCode, splitFields, type PreCheckField } from '@/features/precheck/model';
import { changeKey, type DraftWireChange } from '@/features/records/draft/changeSet';
import type {
  RecordChangeOutcome,
  RecordChangeset,
  RecordConflict,
  RecordPlannedStep,
  RecordPreview,
  RecordPreviewQuery,
} from '@/features/records/editor/types';

import type { OperatorPreCheckBaseline } from './baselines';
import { valuesAfter } from './compile';
import {
  asDefinitions,
  asWaiverRows,
  askedIds,
  definitionNotes,
  DEFINITIONS_PATH,
  fieldListNotes,
  fieldListSummary,
  isCustomRow,
  LABEL_MAX_LENGTH,
  LEAD_FIELDS,
  LIST_PATHS,
  MAX_CUSTOM_FIELDS,
  overlayDefinitions,
  PARTY_FIELDS,
  removeBlockedReason,
  typeLabel,
  WAIVERS_PATH,
  waiverNotes,
} from './model';

/** Saving asks first when a custom field's label or type changes on tours. */
export const CONFIRM_FOLLOW_UPS = ['definition_relabel', 'definition_retype'];

const tours = (n: number) => `${n} ${n === 1 ? 'tour' : 'tours'}`;

/** A waiver's name from the options (or its id). */
export function waiverName(base: OperatorPreCheckBaseline, id: number): string {
  return base.data.options.waivers.find((w) => w.id === id)?.name ?? `Waiver #${id}`;
}

/** Why the server would refuse a field list, row by row ("Field 3: …"), in its words. */
export function listProblems(list: unknown, base: OperatorPreCheckBaseline): string[] {
  // Numbered as the server numbers them: by position in the list.
  const all = asFields(list);
  const codes = new Set(base.data.options.categories.map((c) => c.code));
  const out: string[] = [];
  all.forEach((f: PreCheckField, i) => {
    const n = i + 1;
    const name = String(f.name ?? '').trim();
    if (isQuestion(f)) {
      if (!codes.has(questionCode(f))) out.push(`Field ${n}: the tag category is not one of this operator's categories.`);
      return;
    }
    if (!name) out.push(`Field ${n}: enter a label.`);
    else if (name.length > LABEL_MAX_LENGTH) out.push(`Field ${n}: the label is too long (${LABEL_MAX_LENGTH} characters at most).`);
  });
  if (all.filter((f) => !isQuestion(f) && isCustomRow(f)).length > MAX_CUSTOM_FIELDS) out.push(`At most ${MAX_CUSTOM_FIELDS} custom fields per form.`);
  return out;
}

/** Why the server would refuse the waiver rows ("Waiver 2: …"). */
export function waiverProblems(rows: unknown, base: OperatorPreCheckBaseline): string[] {
  const stored = new Set(asWaiverRows(base.paths[base.rec]?.[WAIVERS_PATH]).map((w) => w.waiver_id));
  const own = new Map(base.data.options.waivers.map((w) => [w.id, w]));
  const seen = new Map<number, number>();
  const out: string[] = [];
  asWaiverRows(rows).forEach((w, i) => {
    const n = i + 1;
    const opt = own.get(w.waiver_id);
    if (!opt) out.push(`Waiver ${n}: not one of this operator's waivers.`);
    else if (!opt.enabled && !stored.has(w.waiver_id)) out.push(`Waiver ${n}: this waiver is disabled.`);
    else if (seen.has(w.waiver_id)) out.push(`Waiver ${n}: the same waiver as waiver ${seen.get(w.waiver_id)}.`);
    else seen.set(w.waiver_id, n);
  });
  return out;
}

/** Why the server would refuse the definitions as the draft leaves them, per definition:
 *  each message named for the ledger ("“Shoe size”: …") and keyed by the field's id (its
 *  row). `lists`: the lists after the change. */
export function definitionProblems(
  to: unknown,
  lists: readonly unknown[],
  base: OperatorPreCheckBaseline,
): Array<{ id: string; text: string }> {
  const after = asDefinitions(to);
  const ids = new Set(after.map((d) => d.id));
  const asked = askedIds(lists);
  const out: Array<{ id: string; text: string }> = [];
  for (const d of after) {
    const meta = base.definitions.get(d.id);
    const label = d.name.trim() || meta?.name || d.id;
    if (!d.name.trim()) out.push({ id: d.id, text: `“${meta?.name ?? d.id}”: Enter a label.` });
    else if (d.name.trim().length > LABEL_MAX_LENGTH) out.push({ id: d.id, text: `“${label}”: Too long (${LABEL_MAX_LENGTH} characters at most).` });
    if (meta && meta.type !== d.type && meta.answers > 0) {
      out.push({
        id: d.id,
        text: `“${label}”: Can't change the type: ${meta.answers} ${meta.answers === 1 ? 'answer is' : 'answers are'} stored for this field.`,
      });
    }
  }
  for (const [id, meta] of base.definitions) {
    if (ids.has(id)) continue;
    const why = removeBlockedReason(meta, asked.has(id));
    if (why) out.push({ id, text: `“${meta.name}”: ${why}` });
  }
  return out;
}

export function operatorLocalPlan(changes: readonly DraftWireChange[], base: OperatorPreCheckBaseline): RecordPreview {
  const rec = base.rec;
  const stored = base.paths[rec] ?? {};
  const after = valuesAfter(changes, base);
  const defs = asDefinitions(after(DEFINITIONS_PATH));
  const touched = new Set(changes.filter((c) => `${c.entity}:${c.id}` === rec && c.field).map((c) => c.field!));

  const outcomes: RecordChangeOutcome[] = changes.map((c) => {
    const path = c.field ?? c.action ?? '';
    let notes: string[] = [];
    if ((LIST_PATHS as readonly string[]).includes(path)) {
      notes = fieldListNotes(c.from, c.to, defs);
      // One item, already the row's own words (`describeChange`): not said twice.
      if (notes.length === 1 && fieldListSummary(c.from, c.to, defs) !== null) notes = [];
    }
    else if (path === WAIVERS_PATH) notes = waiverNotes(c.from, c.to, (id) => waiverName(base, id));
    else if (path === DEFINITIONS_PATH) notes = definitionNotes(c.from, c.to);
    return {
      key: changeKey(`${c.entity}:${c.id}`, path),
      entity: c.entity,
      id: c.id,
      field: c.field ?? null,
      action: c.action ?? null,
      outcome: 'change',
      from: c.from,
      to: c.to,
      verdict: null,
      ...(notes.length > 0 ? { notes } : {}),
    };
  });

  const conflicts: RecordConflict[] = [];
  const conflict = (path: string, detail: string) => conflicts.push({ code: 'invalid', key: changeKey(rec, path), detail });
  for (const path of LIST_PATHS) {
    if (touched.has(path)) for (const p of listProblems(overlayDefinitions(after(path), defs), base)) conflict(path, p);
  }
  if (touched.has(WAIVERS_PATH)) for (const p of waiverProblems(after(WAIVERS_PATH), base)) conflict(WAIVERS_PATH, p);
  if (touched.has(DEFINITIONS_PATH)) {
    for (const p of definitionProblems(after(DEFINITIONS_PATH), [after(LEAD_FIELDS), after(PARTY_FIELDS)], base)) {
      conflicts.push({ code: 'invalid', key: changeKey(rec, DEFINITIONS_PATH), detail: p.text, contact_key: p.id });
    }
  }

  const followUps: RecordPlannedStep[] = [];
  if (changes.length > 0) {
    if (!base.data.stored) {
      followUps.push({ code: 'precheck_created', label: 'Creates the operator’s pre-check-in settings — none are stored yet' });
    }
    const inheriting = base.data.reach.inheriting_tours;
    followUps.push({
      code: 'inheriting_tours',
      label: 'Tours that inherit these settings get the change',
      detail: inheriting > 0 ? tours(inheriting) : 'no tour inherits them yet',
    });
  }
  // One line per kind (a follow-up's code is its key and what Save confirms).
  const join = (parts: string[]) => parts.join('; ');
  if (touched.has(DEFINITIONS_PATH)) {
    const was = new Map(asDefinitions(stored[DEFINITIONS_PATH]).map((d) => [d.id, d]));
    const relabels: string[] = [];
    const retypes: string[] = [];
    let relabelTours = 0;
    let retypeTours = 0;
    for (const d of defs) {
      const old = was.get(d.id);
      const n = base.definitions.get(d.id)?.used_by.tours ?? 0;
      if (!old || n === 0) continue;
      if (old.name !== d.name) {
        relabels.push(`“${old.name}” → “${d.name}” (${tours(n)})`);
        relabelTours += n;
      }
      if (old.type !== d.type) {
        retypes.push(`“${d.name}” → ${typeLabel(d.type)} (${tours(n)})`);
        retypeTours += n;
      }
    }
    if (relabels.length > 0) {
      followUps.push({
        code: 'definition_relabel',
        label: `Relabels ${relabels.length === 1 ? 'a custom field' : 'custom fields'} on every tour that asks ${relabels.length === 1 ? 'it' : 'them'}: ${join(relabels)}`,
        detail: tours(relabelTours),
      });
    }
    if (retypes.length > 0) {
      followUps.push({
        code: 'definition_retype',
        label: `Changes the type on every tour that asks ${retypes.length === 1 ? 'it' : 'them'}: ${join(retypes)}`,
        detail: tours(retypeTours),
      });
    }
  }
  // A standard field's label is the operator's too (one Pre_Check_In_Field per id).
  const relabelled = new Map<string, string>();
  for (const path of LIST_PATHS) {
    if (!touched.has(path)) continue;
    const was = new Map(splitFields(stored[path]).regular.map((f) => [f.id, f.name]));
    for (const f of splitFields(after(path)).regular) {
      const old = was.get(f.id);
      if (old !== undefined && old !== f.name && !isCustomRow(f)) relabelled.set(f.id, `“${old}” → “${f.name}”`);
    }
  }
  if (relabelled.size > 0) {
    const one = relabelled.size === 1;
    followUps.push({
      code: 'field_relabel',
      label: `Relabels ${one ? 'a standard field' : 'standard fields'} on every tour of this operator that asks ${one ? 'it' : 'them'}: ${join([...relabelled.values()])}`,
    });
  }

  return {
    changes: outcomes,
    required_choices: [],
    conflicts,
    effects: {},
    events: [],
    external: [],
    follow_ups: followUps,
    warnings: [],
  };
}

/** `RecordEditorConfig.usePreview` for the tab: the local plan of the body, at once. */
export function useOperatorLocalPlan(body: RecordChangeset | null, base: OperatorPreCheckBaseline): RecordPreviewQuery {
  const data = useMemo(() => (body ? operatorLocalPlan(body.changes as DraftWireChange[], base) : undefined), [body, base]);
  return { data, isFetching: false, isPlaceholderData: false, error: null };
}
